import Turbolinks from 'turbolinks';
import moment from 'moment';
import { all, call, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { normalizePath } from '@elab/core/utils/url';
import { isASM } from '@elab/core/utils/ASM';
import { actions as addressActions, selectors as addressSelectors } from '@elab/core/store/address';
import { selectors as configSelectors } from '@elab/core/store/config';
import { adjustMaxAllowedProducts, pageType } from '@elab/core/utils/constants';
import { actions as registrationActions } from '@elab/components/connected/forms/RegistrationForm/redux';
import { hashString } from '@elab/core/utils/helpers';

import analytics from '../../utils/analytics';
import * as address from '../../utils/address';
import * as storage from '../../utils/storage';
import { checkForHtmlContent } from '../../utils/checkHtmlContent';
import { createWatcher } from '../core/saga';
import globalActions from '../actions';
import { actions as searchActions } from '../search';
import {
    actions as analyticsActions,
    selectors as analyticsSelectors,
    types as analyticsTypes,
} from '../analytics';
import { actions as userActions, selectors as userSelectors, types as userTypes } from '../user';
import { actions as cartActions } from '../cart';
import { actions as uiActions } from '../ui';
import { actions as authActions, selectors as authSelectors } from '../auth';
import { actions as orderActions, selectors as orderSelectors } from '../order';
import { actions as socialActions } from '../social';
import { types as coreTypes } from '../core';
import logger from '../../utils/logger';
import { toggleConsentPopup } from '../../utils/oneTrust';

import { getConfiguration, getSelectedLanguageContent, setCustomerLanguage } from './api';

import { actions, selectors, types } from '.';

export function* clearAddressMessageIfNeeded() {
    const wasShown = yield select(addressSelectors.getMessageWasShown);

    if (wasShown) {
        yield put(addressActions.removeMessage());
    }
}

export function* redirect({ payload }) {
    if (typeof payload === 'object' && payload.successRedirectUrl) {
        yield call(
            logger.warn,
            'Use of deprecated object in redirect:',
            payload.successRedirectUrl,
        );
    }

    let url = payload;
    let shouldReloadPage = false;
    if (typeof payload === 'object') {
        url = payload.successRedirectUrl || payload.url;
        shouldReloadPage = payload.shouldReloadPage;
    }
    if (!url) {
        return;
    }

    yield put(analyticsActions.setPageload(false));

    const isTurbolinksEnabled = yield select(selectors.isTurbolinksEnabled);
    // IMPORTANT NOTE: This is just a temporary fix and it must be refactored in the future.
    // If the flag checkContentType was passed along with payload and its value equals to true
    // we will make GET request to the redirect URL to check if there is HTML content.
    // In case we have content type of text/html in response
    // Turbolinks will be used, otherwise location api will be used.
    const canBeMerged = !payload.checkContentType || (yield call(checkForHtmlContent, url));

    if (!shouldReloadPage && isTurbolinksEnabled && canBeMerged) {
        yield call(Turbolinks.visit, url);
    } else {
        yield call(address.redirect, url);
    }

    if (payload && payload.settings && payload.settings.targetToScrollTo) {
        if (payload.settings.actionToWaitBeforeScroll) {
            yield take(payload.settings.actionToWaitBeforeScroll);
        } else {
            yield take(types.PAGE_LOAD);
        }
        const target = document.querySelector(payload.settings.targetToScrollTo);
        yield put(uiActions.scrollToTarget({ target }));
    }
}

export function* goto({ payload }) {
    yield put(analyticsActions.setPageload(false));
    yield call(clearAddressMessageIfNeeded);

    const isTurbolinksEnabled = yield select(selectors.isTurbolinksEnabled);

    if (isTurbolinksEnabled && !payload.reload) {
        yield call(Turbolinks.visit, payload.url);
    } else {
        yield call(address.redirect, payload.url);
    }
}

export function* openURL({ payload }) {
    const { url, isNewTab } = payload;
    yield call(address.openURL, url, isNewTab);
}

export function* beforeRender() {
    yield put(authActions.checkUserToken());
    yield call(updateMessages);
}

export function* backupMessage({ payload }) {
    if (!isASM()) {
        return;
    }

    let shouldBackup = [
        'addressBookMessage',
        'emailChangeSuccessMessage',
        'passwordChangeSuccessMessage',
    ].some(msg => msg === payload.id);
    if (shouldBackup) {
        yield call(storage.set, storage.key.globalMessage, payload, true);
    }
}

export function* restoreBackupedMessage() {
    const msg = yield call(storage.get, storage.key.globalMessage, true);
    if (msg) {
        yield put(actions.addMessage(msg));
        yield delay(1000);
        yield call(storage.remove, storage.key.globalMessage);
    }
}

export function* onLoad() {
    yield put(uiActions.closeDialogByName('product-cart-popup'));
    yield put(uiActions.closeDialogByName(adjustMaxAllowedProducts));
    const time = yield call([Date, 'now']);
    yield put(actions.setLastTimePageLoad(time));

    yield call(setAnalyticsPageInfo);

    const ignoreResetOnLoad = yield select(configSelectors.shouldIgnoreResetOnLoad);
    if (ignoreResetOnLoad) {
        yield put(actions.closeCountryDialog(false));
        yield put(actions.closeLanguageDialog(false));
        yield put(uiActions.toggleMinicartPopover(false));
        yield put(cartActions.updateCartComplete(false));
    }

    if (yield select(selectors.isSubmitting)) {
        yield put(actions.setSubmitting(false));
    }

    const hash = yield call(address.getHash);
    if (hash) {
        yield put(uiActions.scrollToTop({ target: hash }));
    }

    const isTurbolinksEnabled = yield select(selectors.isTurbolinksEnabled);

    if (isTurbolinksEnabled && window.Optanon && !window.Optanon.IsAlertBoxClosed()) {
        delete document.documentElement.dataset.triggered;
        window.Optanon.LoadBanner();
    }

    if (isASM()) {
        yield call(restoreBackupedMessage);
    }

    let scrollPageAfterLoad = yield select(configSelectors.shouldScrollAfterLoad);
    if (scrollPageAfterLoad) {
        const hardReload = document.head.querySelector('[data-turbolinks-track="reload"]');
        hardReload && window.scrollTo(0, 0);
    }
}

export function* setAnalyticsPageInfo() {
    let pageInfo = {};
    let hasPageLoadInfo = yield select(configSelectors.getPageLoadInformation);
    if (hasPageLoadInfo) {
        pageInfo = yield take(analyticsTypes.SET_PAGE_LOAD_INFO);
    }

    if (pageInfo.payload) {
        yield put(
            actions.setGlobalPageInfo({
                current: pageInfo.payload.location,
                previousPage: pageInfo.payload.referrer,
            }),
        );
    }

    if (pageInfo.payload && pageInfo.payload.pageType !== 'account page') {
        const fbAuthId = yield select(authSelectors.getFbAuthId);
        if (fbAuthId) {
            sessionStorage.removeItem(`fbssls_${fbAuthId}`);
            yield put(socialActions.setInitialSocialInfo());
            yield put(registrationActions.setDataFieldValue('socialProfileId', null, true));
            yield put(
                registrationActions.setDataFieldValue('socialProfileAccessToken', null, true),
            );
            yield put(registrationActions.setDataFieldValue('socialProfileProvider', null, true));
        }

        const googleSecret = sessionStorage.removeItem('googleAuthToken');
        if (googleSecret) {
            sessionStorage.removeItem('googleAuthToken');
            yield put(resetSocialFormData);
        }
    }
    const customerLoginStatus =
        pageInfo && pageInfo.payload && pageInfo.payload.customerLoginStatus;
    const doSetUserId = yield select(configSelectors.doSetUserId);
    const showUserParams = yield select(configSelectors.showUserParams);
    if (doSetUserId) {
        let customerId = yield select(userSelectors.getCustomerId);
        let customerSegment = yield select(userSelectors.getCustomerSegment);
        const waitForSetProfile = yield select(configSelectors.waitForSetProfile);
        if (waitForSetProfile && !customerId && customerLoginStatus === 'logged') {
            yield take(userTypes.SET_PROFILE);
            customerId = yield select(userSelectors.getCustomerId);
            customerSegment = yield select(userSelectors.getCustomerSegment);
        }
        pageInfo.payload.userId = customerId;
        pageInfo.payload.segments = customerSegment.map(segment => segment.code);
    }
    const userParams = {};
    if (showUserParams) {
        userParams.ueh = yield call(hashString, yield select(userSelectors.getUserEmail));
        userParams.ufh = yield call(hashString, yield select(userSelectors.getUserFirstName));
        userParams.ush = yield call(hashString, yield select(userSelectors.getUserLastName));
        userParams.uph = yield call(hashString, yield select(userSelectors.getUserPhone));
        userParams.ugh = yield call(hashString, yield select(userSelectors.getUserGender));
        const waitForSetProfile = yield select(configSelectors.waitForSetProfile);
        if (waitForSetProfile && !userParams.ueh && customerLoginStatus === 'logged') {
            yield take(userTypes.SET_PROFILE);
            userParams.ueh = yield call(hashString, yield select(userSelectors.getUserEmail));
            userParams.ufh = yield call(hashString, yield select(userSelectors.getUserFirstName));
            userParams.ush = yield call(hashString, yield select(userSelectors.getUserLastName));
            userParams.uph = yield call(hashString, yield select(userSelectors.getUserPhone));
            userParams.ugh = yield call(hashString, yield select(userSelectors.getUserGender));
        }
        pageInfo.payload.user_params = userParams;
    }
    // add bloomreach catalog name to page load event
    const brTracking = yield select(configSelectors.brTracking);
    if (hasPageLoadInfo && brTracking) {
        pageInfo.payload.brCatalogName = yield select(selectors.getBrCatalogName);
    }
    yield call(analytics.handlePageLoad, pageInfo.payload);
    if (pageInfo.payload && pageInfo.payload.pageType === 'order confirmation page') {
        const proceedPageLoad = yield select(analyticsSelectors.getProceedPageLoad);
        if (!proceedPageLoad) {
            yield take(analyticsTypes.PROCEED_PAGELOAD);
        }
    }
    yield put(analyticsActions.setPageload(true));
    yield put(actions.pageLoadEventPushed());
}

export function* resetSocialFormData() {
    yield put(socialActions.setInitialSocialInfo());
    yield put(registrationActions.setDataFieldValue('socialProfileId', null, true));
    yield put(registrationActions.setDataFieldValue('socialProfileAccessToken', null, true));
    yield put(registrationActions.setDataFieldValue('socialProfileProvider', null, true));
}

export function* redirectPageSelectors({ payload }) {
    yield put(searchActions.setFilterLoading(true));
    try {
        if (payload) {
            yield put(actions.redirect(payload));
        }
        yield take(types.APP_INIT);
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(searchActions.setFilterLoading(false));
    }
}

export function* appInit(action) {
    const oldSite = yield select(selectors.getSite);
    const oldCountry = yield select(selectors.getCountry);
    const oldLanguage = yield select(selectors.getLanguage);
    const oldOccBaseUri = yield select(selectors.getOccBaseUri);
    const oldWishlistMode = yield select(selectors.getWishlistMode);
    const oldCurrencyCode = yield select(selectors.getCurrencyCode);
    const oldDeliveryCountry = yield select(selectors.getDeliveryCountry);
    const oldbrCatalogName = yield select(selectors.getBrCatalogName);

    const {
        availableLanguages,
        language,
        country,
        preferredLanguage,
        preferredCountry,
        site,
        baseStore,
        occBaseUri,
        wishlistMode,
        customerStatusDelay,
        customerStatusRetry,
        customerStatusBeforeAttemptDelay,
        imagePlaceholderPath,
        currencyCode,
        brCatalogName,
    } = action.payload;

    if (oldbrCatalogName !== brCatalogName) {
        yield put(actions.setBrCatalogName(brCatalogName));
    }

    if (
        oldSite === site &&
        oldCountry === country &&
        oldLanguage === language &&
        oldWishlistMode === wishlistMode &&
        oldOccBaseUri === occBaseUri &&
        oldCurrencyCode === currencyCode
    ) {
        return;
    }
    yield put(actions.setLanguage(language));
    yield put(actions.setCountry(country));
    yield put(actions.setWishlistMode(wishlistMode));
    if (oldDeliveryCountry === '') {
        yield put(actions.setDeliveryCountry(country));
    }
    if (customerStatusDelay) {
        yield put(userActions.setCustomerStatusDelay(customerStatusDelay));
    }
    if (customerStatusRetry) {
        yield put(userActions.setCustomerStatusRetry(customerStatusRetry));
    }
    if (customerStatusBeforeAttemptDelay) {
        yield put(
            userActions.setCustomerStatusBeforeAttemptDelay(customerStatusBeforeAttemptDelay),
        );
    }
    yield put(actions.setAvailableLanguages(availableLanguages));
    yield put(actions.setSite(site));
    yield put(actions.setBaseStore(baseStore));
    yield put(actions.setOccBaseUri(occBaseUri));
    yield put(actions.setImagePlaceholderPath(imagePlaceholderPath));
    yield put(actions.setCurrencyCode(currencyCode));
    analytics.currencyCode = () => currencyCode;

    const urlArray = (yield call(address.getPathname)).split('/');
    const urlLang = availableLanguages.find(lang => urlArray.length > 1 && lang === urlArray[1]);

    if (preferredLanguage || urlLang) {
        yield put(actions.setPreferredLanguage(preferredLanguage || urlLang));
    }

    const urlParamCountry = yield call(address.getSearchParam, address.key.country);

    if (preferredCountry || urlParamCountry) {
        yield put(actions.setPreferredCountry(preferredCountry || urlParamCountry));
    }

    yield put(globalActions.init());
}

const setCountry = ({ payload = {} }) => storage.set(storage.key.country, payload);

const setLocale = ({ payload = {} }) => {
    moment.locale(payload);
    storage.set(storage.key.language, payload);
};

export function* updateCountry({ payload = {} }) {
    const { successRedirectUrl = '' } = payload;
    if (!successRedirectUrl) {
        return;
    }

    const preferredCountry = yield call(
        address.getParamFromURL,
        successRedirectUrl,
        address.key.country,
    );

    if (!preferredCountry) {
        return;
    }
    yield put(actions.setPreferredCountry(preferredCountry));
    const siteCountry = yield select(selectors.getCountry);
    if (siteCountry !== preferredCountry) {
        yield put(actions.redirect(successRedirectUrl));
    }
    yield put(uiActions.closeDialog());
}

export function* changeLanguage({ payload = {} }) {
    const { code, langUrl, multiLanguage = false, powerReviewEnabled = false } = payload;
    let shouldReloadPage = false;
    const data = `code=${code}`;
    const orderCode = yield select(orderSelectors.getOrderCode);

    yield put(actions.setLanguage(code));

    if (orderCode && multiLanguage) {
        yield put(orderActions.loadOrder(orderCode));
    }
    if (powerReviewEnabled) {
        const currentPage = yield select(analyticsSelectors.getPageType);
        if (currentPage === pageType.pdp) shouldReloadPage = true;
    }

    const redirectUrl = yield call(getSelectedLanguageContent, {
        langUrl,
        data,
    });

    const isAnonymous = yield select(userSelectors.isAnonymous);
    if (!isAnonymous) {
        yield call(setCustomerLanguage, code);
    }
    yield put(userActions.loadProfile());
    if (redirectUrl) {
        yield put(actions.redirect({ url: redirectUrl, shouldReloadPage }));
    }
}

export function* updateLanguage({ payload = {} }) {
    const { successRedirectUrl = '', languageIsoCode = '' } = payload;
    const availableLanguages = yield select(selectors.getAvailableLanguages);
    const url = successRedirectUrl || (yield call(address.getFullPath));
    const language = languageIsoCode.split('_')[0];
    const urlPrependedWithLang = yield call(addOrReplaceLangBeforeUrl, {
        url,
        language,
        availableLanguages,
    });

    yield put(actions.setPreferredLanguage(language));
    yield put(actions.redirect(urlPrependedWithLang));
    yield put(actions.setPreferredCountry());
}

function addOrReplaceLangBeforeUrl({ url, language, availableLanguages }) {
    const urlPartsArray = url.split('/');
    const urlWithoutLangPartsArray = urlPartsArray.filter(urlPart => {
        return !availableLanguages.some(lang => lang.indexOf(urlPart) !== -1);
    });
    const urlWithLangPartArray = [language, ...urlWithoutLangPartsArray];
    const urlWithLang = urlWithLangPartArray.join('/');
    return normalizePath(urlWithLang);
}

export function* eraseWithDelayTemporaryMessage({ payload }) {
    yield delay(payload);
    yield put(actions.eraseTemporaryMessage());
}

export function* removeMessageWithDelay({ payload }) {
    if (payload && payload.delay) {
        yield delay(payload.delay);
        yield put(actions.removeMessage(payload));
    }
}

export function* updateMessages() {
    const messages = yield select(selectors.getMessages);
    yield all(
        messages.map(
            message =>
                message &&
                message.clearOnRedirect &&
                message.isShown &&
                put(actions.removeMessage(message)),
        ),
    );
}

export function* formInitialize({ payload }) {
    const { storeName, action } = payload;

    let store = yield select(state => state[storeName]);

    while (!store) {
        yield take(coreTypes.ADD_SAGA);
        store = yield select(state => state[storeName]);
    }

    yield put(action);
}

export function* openCookiePopup() {
    yield call(toggleConsentPopup);
}

export function* getStoreConfiguration({ payload }) {
    try {
        const configurations = yield call(getConfiguration, payload);
        const { key, value } = configurations;
        yield put(actions.setStoreConfiguration({ key, value }));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export default [
    createWatcher(types.GO_BACK, address.goBack),
    createWatcher(types.SET_PREFERRED_COUNTRY, setCountry),
    createWatcher(types.SET_PREFERRED_LANGUAGE, setLocale),
    takeEvery(types.BEFORE_RENDER, beforeRender),
    takeEvery(types.PAGE_LOAD, onLoad),
    takeEvery(types.REDIRECT, redirect),
    takeEvery(types.GOTO, goto),
    takeEvery(types.APP_INIT, appInit),
    takeEvery(types.UPDATE_COUNTRY, updateCountry),
    takeEvery(types.UPDATE_LANGUAGE, updateLanguage),
    takeLatest(types.REDIRECT_PAGE_SELECTORS, redirectPageSelectors),
    takeEvery(types.FORM_INITIALIZE, formInitialize),
    takeEvery(types.CHANGE_LANGUAGE, changeLanguage),
    takeEvery(types.ERASE_WITH_DELAY_TEMPORARY_MESSAGE, eraseWithDelayTemporaryMessage),
    takeEvery(types.ADD_MESSAGE, backupMessage),
    takeEvery(types.REMOVE_MESSAGE_WITH_DELAY, removeMessageWithDelay),
    takeEvery(types.OPEN_COOKIE_POPUP, openCookiePopup),
    takeEvery(types.OPEN_URL, openURL),
    takeLatest(types.GET_STORE_CONFIG, getStoreConfiguration),
];
